<template>
  <div class="register-page">
    <div class="register-container">
      <div class="register-card">
        <!-- Tenant Logo -->
        <div class="register-logo">
          <img
            v-if="tenantLogo"
            :src="tenantLogo"
            :alt="tenantName ?? ''"
            class="logo-image"
          />
          <div v-else class="logo-placeholder">
            {{ tenantName || 'Franchise Factory' }}
          </div>
        </div>

        <div class="register-header">
          <h1>{{ t('Register') }}</h1>
          <p class="subtitle">{{ t('Create your account') }}</p>
        </div>

        <form @submit.prevent="handleRegister" class="register-form">
          <!-- Error Message -->
          <div v-if="errorMessage" class="alert alert-error">
            {{ errorMessage }}
          </div>

          <!-- Success Message -->
          <div v-if="successMessage" class="alert alert-success">
            {{ successMessage }}
          </div>

          <!-- Name Field -->
          <div class="form-group">
            <label for="name">{{ t('Full Name') }}</label>
            <input
              id="name"
              v-model="formData.name"
              type="text"
              :placeholder="t('Enter your full name')"
              required
              autocomplete="name"
              :disabled="isLoading || registrationSuccess"
              data-testid="auth-register-name"
            />
          </div>

          <!-- Username Field -->
          <div class="form-group">
            <label for="username">{{ t('Username') }}</label>
            <input
              id="username"
              v-model="formData.username"
              type="text"
              :placeholder="t('Choose a username')"
              required
              autocomplete="username"
              :disabled="isLoading || registrationSuccess"
              data-testid="auth-register-username"
            />
          </div>

          <!-- Email Field -->
          <div class="form-group">
            <label for="email">{{ t('Email') }}</label>
            <input
              id="email"
              v-model="formData.email"
              type="email"
              :placeholder="t('Enter your email')"
              required
              autocomplete="email"
              :disabled="isLoading || registrationSuccess"
              data-testid="auth-register-email"
            />
          </div>

          <!-- Password Field -->
          <div class="form-group">
            <label for="password">{{ t('Password') }}</label>
            <input
              id="password"
              v-model="formData.password"
              type="password"
              :placeholder="t('Choose a strong password')"
              required
              autocomplete="new-password"
              minlength="8"
              :disabled="isLoading || registrationSuccess"
              data-testid="auth-register-password"
            />
            <small class="help-text">{{ t('Minimum 8 characters') }}</small>
          </div>

          <!-- Confirm Password Field -->
          <div class="form-group">
            <label for="confirmPassword">{{ t('Confirm Password') }}</label>
            <input
              id="confirmPassword"
              v-model="confirmPassword"
              type="password"
              :placeholder="t('Confirm your password')"
              required
              autocomplete="new-password"
              :disabled="isLoading || registrationSuccess"
              data-testid="auth-register-confirm-password"
            />
          </div>

          <!-- Submit Button -->
          <button
            v-if="!registrationSuccess"
            type="submit"
            class="btn btn-primary btn-block"
            :disabled="isLoading"
            data-testid="auth-register-submit"
          >
            <span v-if="!isLoading">{{ t('Create Account') }}</span>
            <span v-else>{{ t('Creating account...') }}</span>
          </button>

          <!-- Go to Confirmation Button -->
          <button
            v-else
            type="button"
            class="btn btn-success btn-block"
            @click="goToConfirm"
            data-testid="auth-register-go-to-confirm"
          >
            {{ t('Continue to Account Confirmation') }}
          </button>
        </form>

        <!-- Login Link -->
        <div class="register-footer">
          <p>
            {{ t('Already have an account?') }}
            <router-link to="/login" class="login-link">
              {{ t('Sign In') }}
            </router-link>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth, type RegisterUserInput } from '@/composables/useAuth'
import { tenantConfig } from '@/services/tenantConfigService'
import { log } from '@/utils/DebugLogger'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const { register } = useAuth()

// Tenant branding
const tenantLogo = computed<string | null>(() => tenantConfig.get<string>('logo_url'))
const tenantName = computed<string | null>(() => tenantConfig.get<string>('tenant_name'))
const primaryColor = computed<string>(() => tenantConfig.get<string>('primary_color') || '#667eea')
const secondaryColor = computed<string>(() => tenantConfig.get<string>('secondary_color') || '#764ba2')

// Form state
const formData = ref<RegisterUserInput>({
  name: '',
  username: '',
  email: '',
  password: ''
})
const confirmPassword = ref<string>('')
const isLoading = ref<boolean>(false)
const errorMessage = ref<string>('')
const successMessage = ref<string>('')
const registrationSuccess = ref<boolean>(false)

onMounted(() => {
  log('RegisterPage', 'onMounted', 'Register page loaded', {
    redirectTo: route.query.redirectTo
  })
})

/**
 * Handle registration form submission
 */
async function handleRegister(): Promise<void> {
  try {
    isLoading.value = true
    errorMessage.value = ''
    successMessage.value = ''

    // Validate password match
    if (formData.value.password !== confirmPassword.value) {
      errorMessage.value = t('Passwords do not match')
      isLoading.value = false
      return
    }

    log('RegisterPage', 'handleRegister', 'Attempting registration', {
      email: formData.value.email,
      username: formData.value.username
    })

    const result = await register(formData.value)

    if (result.success) {
      log('RegisterPage', 'handleRegister', '✅ Registration successful')

      registrationSuccess.value = true
      successMessage.value = t('Account created successfully! Please check your email for verification code.')
    } else {
      log('RegisterPage', 'handleRegister', '❌ Registration failed:', result.error)
      errorMessage.value = result.error || t('Registration failed. Please try again.')
    }
  } catch (error) {
    log('RegisterPage', 'handleRegister', '❌ Registration error:', error)
    errorMessage.value = t('An error occurred. Please try again.')
  } finally {
    isLoading.value = false
  }
}

/**
 * Navigate to confirmation page
 */
function goToConfirm(): void {
  const redirectQuery = route.query.redirectTo
  const redirectTo = typeof redirectQuery === 'string' ? redirectQuery : '/'
  router.push({
    path: '/confirm',
    query: {
      username: formData.value.username,
      redirectTo
    }
  })
}
</script>

<style scoped>
.register-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: v-bind(primaryColor);
  padding: 1rem;
}

.register-container {
  width: 100%;
  max-width: 480px;
}

.register-card {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
}

.register-logo {
  text-align: center;
  margin-bottom: 1.5rem;
}

.logo-image {
  max-width: 180px;
  max-height: 80px;
  height: auto;
  object-fit: contain;
}

.logo-placeholder {
  font-size: 1.5rem;
  font-weight: 700;
  color: v-bind(primaryColor);
  padding: 1rem;
}

.register-header {
  text-align: center;
  margin-bottom: 2rem;
}

.register-header h1 {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.5rem;
}

.subtitle {
  color: #718096;
  font-size: 0.875rem;
}

.register-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 500;
  color: #2d3748;
  font-size: 0.875rem;
}

.form-group input {
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: v-bind(primaryColor);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-group input:disabled {
  background-color: #f7fafc;
  cursor: not-allowed;
}

.help-text {
  color: #718096;
  font-size: 0.75rem;
  margin-top: -0.25rem;
}

.btn {
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: v-bind(secondaryColor);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-success {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white;
}

.btn-success:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(72, 187, 120, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-block {
  width: 100%;
}

.alert {
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
}

.alert-error {
  background-color: #fed7d7;
  color: #c53030;
  border: 1px solid #fc8181;
}

.alert-success {
  background-color: #c6f6d5;
  color: #22543d;
  border: 1px solid #68d391;
}

.register-footer {
  margin-top: 1.5rem;
  text-align: center;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.register-footer p {
  color: #718096;
  font-size: 0.875rem;
}

.login-link {
  color: v-bind(primaryColor);
  font-weight: 500;
  text-decoration: none;
  transition: color 0.2s;
}

.login-link:hover {
  color: v-bind(secondaryColor);
  text-decoration: underline;
}
</style>
